import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertTriangle, Users, Brain, Network, Activity } from "lucide-react";

interface WorkerProfile {
  id: string;
  display_name: string;
  full_name: string;
  position: string;
  team: string;
  current_risk_score: number;
  baseline_risk_score: number;
  total_xp: number;
  consecutive_safe_days: number;
  verified_incidents_count: number;
  network_centrality: number;
  network_role: string;
  clustering_coefficient: number;
  risky_neighbor_count: number;
  safety_influence_score: number;
  is_wide_bridge: boolean;
  psychometric_profile: any;
  contagion_risk_score: number;
  years_of_experience: number;
  credibility_score: number;
}

interface NetworkConnection {
  id: string;
  connection_type: string;
  connection_weight: number;
  other_worker_name: string;
  direction: 'outgoing' | 'incoming'; // outgoing = worker_a, incoming = worker_b
}

interface UserDetailDialogProps {
  workerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserDetailDialog = ({ workerId, open, onOpenChange }: UserDetailDialogProps) => {
  const [profile, setProfile] = useState<WorkerProfile | null>(null);
  const [connections, setConnections] = useState<NetworkConnection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workerId && open) {
      loadWorkerData();
    }
  }, [workerId, open]);

  const loadWorkerData = async () => {
    setLoading(true);
    try {
      const { data: workerData, error } = await supabase
        .from("worker_profiles")
        .select("*")
        .eq("id", workerId)
        .single();

      if (error) throw error;
      setProfile(workerData);

      // Get connections where this worker is worker_a
      const { data: connectionsA } = await supabase
        .from("worker_network_connections")
        .select(`id, connection_type, connection_weight, worker_b`)
        .eq("worker_a", workerId);

      // Get connections where this worker is worker_b
      const { data: connectionsB } = await supabase
        .from("worker_network_connections")
        .select(`id, connection_type, connection_weight, worker_a`)
        .eq("worker_b", workerId);

      const allConnections: NetworkConnection[] = [];
      
      // Process worker_a connections (this worker is worker_a)
      if (connectionsA && connectionsA.length > 0) {
        const workerBIds = connectionsA.map(c => c.worker_b).filter(Boolean);
        const { data: workerNames } = await supabase
          .from("worker_profiles")
          .select("id, display_name")
          .in("id", workerBIds);
        const nameMap = new Map(workerNames?.map(w => [w.id, w.display_name]) || []);
        
        connectionsA.forEach(c => {
          allConnections.push({
            id: c.id,
            connection_type: c.connection_type,
            connection_weight: c.connection_weight || 1,
            other_worker_name: nameMap.get(c.worker_b) || "Unknown",
            direction: 'outgoing' // This worker is worker_a
          });
        });
      }

      // Process worker_b connections (this worker is worker_b)
      if (connectionsB && connectionsB.length > 0) {
        const workerAIds = connectionsB.map(c => c.worker_a).filter(Boolean);
        const { data: workerNames } = await supabase
          .from("worker_profiles")
          .select("id, display_name")
          .in("id", workerAIds);
        const nameMap = new Map(workerNames?.map(w => [w.id, w.display_name]) || []);
        
        connectionsB.forEach(c => {
          // For non-supervisor connections, check for duplicates (bidirectional connections)
          const exists = c.connection_type !== 'supervisor' && allConnections.some(
            existing => existing.connection_type === c.connection_type && 
                        existing.other_worker_name === nameMap.get(c.worker_a)
          );
          if (!exists) {
            allConnections.push({
              id: c.id,
              connection_type: c.connection_type,
              connection_weight: c.connection_weight || 1,
              other_worker_name: nameMap.get(c.worker_a) || "Unknown",
              direction: 'incoming' // This worker is worker_b
            });
          }
        });
      }

      setConnections(allConnections);
    } catch (error) {
      console.error("Error loading worker data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role: string | null) => {
    const roleConfig: Record<string, { color: string; label: string }> = {
      champion: { color: "bg-success text-success-foreground", label: "Champion" },
      bridge: { color: "bg-primary text-primary-foreground", label: "Bridge" },
      influencer: { color: "bg-secondary text-secondary-foreground", label: "Influencer" },
      learner: { color: "bg-warning text-warning-foreground", label: "Learner" },
      isolate: { color: "bg-muted text-muted-foreground", label: "Isolate" }
    };
    const config = roleConfig[role || "learner"] || roleConfig.learner;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getRiskColor = (score: number) => {
    if (score >= 70) return "text-destructive";
    if (score >= 40) return "text-warning";
    return "text-success";
  };

  const psychProfile = profile?.psychometric_profile as any;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !profile ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Worker not found</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div>
                  <DialogTitle className="text-xl">{profile.full_name || profile.display_name}</DialogTitle>
                  <p className="text-sm text-muted-foreground">{profile.position || profile.team}</p>
                </div>
                {getRoleBadge(profile.network_role)}
              </div>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4 py-4 border-b">
              <div>
                <div className="text-sm text-muted-foreground">Department</div>
                <div className="font-medium">{profile.team}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Position</div>
                <div className="font-medium">{profile.position || "—"}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Credibility</div>
                <div className="font-medium">{profile.credibility_score || 100}/100</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Risk Score</div>
                <div className={`font-bold text-lg ${getRiskColor(profile.current_risk_score)}`}>
                  {profile.current_risk_score}
                </div>
              </div>
            </div>

            <Tabs defaultValue="risk" className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="risk" className="text-xs">Risk</TabsTrigger>
                <TabsTrigger value="network" className="text-xs">Network</TabsTrigger>
                <TabsTrigger value="psychometric" className="text-xs">Psychometric</TabsTrigger>
                <TabsTrigger value="connections" className="text-xs">Connections</TabsTrigger>
              </TabsList>

              {/* Risk Score Breakdown */}
              <TabsContent value="risk" className="space-y-4 mt-4">
                <div className="p-4 rounded-lg border">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-sm">
                    <Brain className="h-4 w-4" />
                    Baseline Risk (Psychometric)
                  </h4>
                  <div className="flex items-center gap-3">
                    <Progress value={profile.baseline_risk_score} className="flex-1" />
                    <span className="font-medium w-12 text-right">{profile.baseline_risk_score}</span>
                  </div>
                </div>

                <div className="p-4 rounded-lg border">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-sm">
                    <Activity className="h-4 w-4" />
                    Behavioral Factors
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground">Verified Incidents</div>
                      <div className="text-lg font-bold text-destructive">{profile.verified_incidents_count}</div>
                    </div>
                    <div className="p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground">Safe Days Streak</div>
                      <div className="text-lg font-bold text-success">{profile.consecutive_safe_days}</div>
                    </div>
                    <div className="p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground">Years Experience</div>
                      <div className="text-lg font-bold">{profile.years_of_experience || 0}</div>
                    </div>
                    <div className="p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground">Total XP</div>
                      <div className="text-lg font-bold">{profile.total_xp || 0}</div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-sm">
                    <Network className="h-4 w-4" />
                    Network Factors
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground">Risky Neighbors</div>
                      <div className="text-lg font-bold text-warning">{profile.risky_neighbor_count || 0}</div>
                    </div>
                    <div className="p-2 rounded bg-muted/50">
                      <div className="text-xs text-muted-foreground">Contagion Risk</div>
                      <div className="text-lg font-bold">{profile.contagion_risk_score || 0}%</div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Network Metrics */}
              <TabsContent value="network" className="mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg border text-center">
                    <div className="text-xl font-bold">{profile.network_centrality || 0}</div>
                    <div className="text-xs text-muted-foreground">Centrality</div>
                  </div>
                  <div className="p-3 rounded-lg border text-center">
                    <div className="text-xl font-bold">{(profile.clustering_coefficient || 0).toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground">Clustering Coef.</div>
                  </div>
                  <div className="p-3 rounded-lg border text-center">
                    <div className="text-xl font-bold text-success">{profile.safety_influence_score || 0}</div>
                    <div className="text-xs text-muted-foreground">Safety Influence</div>
                  </div>
                  <div className="p-3 rounded-lg border text-center">
                    <div className="text-xl font-bold">{profile.is_wide_bridge ? "Yes" : "No"}</div>
                    <div className="text-xs text-muted-foreground">Wide Bridge</div>
                  </div>
                </div>
                <div className="mt-4 p-3 rounded-lg border">
                  <p className="text-xs text-muted-foreground">
                    {profile.network_role === "champion" && "Safety Champion: High connections with low risk. Ideal for mentoring."}
                    {profile.network_role === "bridge" && "Wide Bridge: Connects multiple teams. Critical for spreading behaviors."}
                    {profile.network_role === "learner" && "Learner: Higher risk. Needs guidance from champions."}
                    {profile.network_role === "isolate" && "Isolate: Few connections. May need integration."}
                    {!profile.network_role && "Role not yet determined."}
                  </p>
                </div>
              </TabsContent>

              {/* Psychometric Profile */}
              <TabsContent value="psychometric" className="mt-4">
                {psychProfile ? (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg border">
                      <h5 className="font-medium mb-3 text-sm">UPPS-P Impulsivity</h5>
                      <div className="space-y-2">
                        {[
                          { key: "lack_of_perseverance", label: "Lack of Perseverance", critical: true },
                          { key: "lack_of_premeditation", label: "Lack of Premeditation" },
                          { key: "sensation_seeking", label: "Sensation Seeking" },
                          { key: "positive_urgency", label: "Positive Urgency" },
                          { key: "negative_urgency", label: "Negative Urgency" }
                        ].map(facet => {
                          const value = psychProfile.upps_p?.[facet.key] || 0;
                          return (
                            <div key={facet.key}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className={facet.critical ? "text-destructive font-medium" : ""}>
                                  {facet.label}
                                </span>
                                <span>{value}%</span>
                              </div>
                              <Progress value={value} className={`h-1.5 ${facet.critical ? "[&>div]:bg-destructive" : ""}`} />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg border">
                        <div className="text-xs text-muted-foreground">DOSPERT Health/Safety</div>
                        <div className="text-xl font-bold">{psychProfile.dospert?.health_safety || 0}%</div>
                      </div>
                      <div className="p-3 rounded-lg border">
                        <div className="text-xs text-muted-foreground">Delay Discounting</div>
                        <div className="text-xl font-bold">{psychProfile.delay_discounting || 0}%</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Brain className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Assessment not completed</p>
                  </div>
                )}
              </TabsContent>

              {/* Connections */}
              <TabsContent value="connections" className="mt-4">
                {connections.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No connections recorded</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {Object.entries(
                      connections.reduce((acc, c) => {
                        acc[c.connection_type] = acc[c.connection_type] || [];
                        acc[c.connection_type].push(c);
                        return acc;
                      }, {} as Record<string, NetworkConnection[]>)
                    ).map(([type, conns]) => {
                      // For supervisor type, show direction-aware labels
                      const getTypeLabel = (t: string, c: NetworkConnection) => {
                        if (t === 'supervisor') {
                          return c.direction === 'outgoing' ? 'Supervised by' : 'Supervises';
                        }
                        return t.replace(/_/g, " ");
                      };
                      
                      return (
                        <div key={type} className="p-2 rounded-lg border">
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant="outline" className="text-xs capitalize">{type.replace(/_/g, " ")}</Badge>
                            <span className="text-xs text-muted-foreground">{conns.length}</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {conns.slice(0, 5).map(c => (
                              <span key={c.id} className="text-xs px-1.5 py-0.5 bg-muted rounded">
                                {type === 'supervisor' && (
                                  <span className="text-muted-foreground mr-1">
                                    {c.direction === 'outgoing' ? '→' : '←'}
                                  </span>
                                )}
                                {c.other_worker_name}
                              </span>
                            ))}
                            {conns.length > 5 && (
                              <span className="text-xs text-muted-foreground">+{conns.length - 5} more</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
